interface KernelBuild {
    release: string;
    version: string;
}

const KERNEL_BUILDS: KernelBuild[] = [
    {
        release: "6.8.0-48-generic",
        version: "#48-Ubuntu SMP PREEMPT_DYNAMIC Fri Sep 27 14:21:12 UTC 2024",
    },
    {
        release: "6.8.0-45-generic",
        version: "#45-Ubuntu SMP PREEMPT_DYNAMIC Tue Sep 10 12:34:17 UTC 2024",
    },
    {
        release: "6.5.0-14-generic",
        version: "#14-Ubuntu SMP PREEMPT_DYNAMIC Sun Nov  5 11:04:32 UTC 2023",
    },
    {
        release: "5.15.0-91-generic",
        version: "#91-Ubuntu SMP Mon Oct 16 06:14:43 UTC 2023",
    },
    {
        release: "5.15.0-113-generic",
        version: "#123-Ubuntu SMP Fri Jun 14 13:34:18 UTC 2024",
    },
    {
        release: "6.2.0-39-generic",
        version: "#40-Ubuntu SMP PREEMPT_DYNAMIC Tue Nov 21 14:12:08 UTC 2023",
    },
    {
        release: "5.19.0-43-generic",
        version: "#44-Ubuntu SMP PREEMPT_DYNAMIC Tue Apr 18 09:29:18 UTC 2023",
    },
];

const ARCHITECTURES = [
    {
        machine: "x86_64",
        processor: "x86_64",
        platform: "x86_64",
    },
    {
        machine: "x86_64",
        processor: "unknown",
        platform: "x86_64",
    },
    {
        machine: "aarch64",
        processor: "aarch64",
        platform: "aarch64",
    },
] as const;

export interface UnameProfile {
    sysname: string;
    nodename: string;
    release: string;
    version: string;
    machine: string;
    processor: string;
    platform: string;
    os: string;
}

function pick<T>(items: readonly T[]): T {
    return items[Math.floor(Math.random() * items.length)]!;
}

export function createUnameProfile(hostname: string): UnameProfile {
    const kernel = pick(KERNEL_BUILDS);
    const arch = pick(ARCHITECTURES);

    return {
        sysname: "Linux",
        nodename: hostname,
        release: kernel.release,
        version: kernel.version,
        machine: arch.machine,
        processor: arch.processor,
        platform: arch.platform,
        os: "GNU/Linux",
    };
}

function parseUnameArgs(input: string): string[] | null {
    const trimmed = input.trim().toLowerCase();
    if (trimmed === "uname") return [];
    if (!trimmed.startsWith("uname ")) return null;

    const argPart = trimmed.slice(5).trim();
    if (!argPart) return [];

    const tokens = argPart.split(/\s+/);
    const flags: string[] = [];

    for (const token of tokens) {
        if (token === "--all") {
            flags.push("a");
            continue;
        }
        if (token.startsWith("--")) {
            const longFlag = token.slice(2);
            if (longFlag === "kernel-name") flags.push("s");
            else if (longFlag === "nodename") flags.push("n");
            else if (longFlag === "kernel-release") flags.push("r");
            else if (longFlag === "kernel-version") flags.push("v");
            else if (longFlag === "machine") flags.push("m");
            else if (longFlag === "processor") flags.push("p");
            else if (longFlag === "hardware-platform") flags.push("i");
            else if (longFlag === "operating-system") flags.push("o");
            continue;
        }
        if (token.startsWith("-")) {
            for (const ch of token.slice(1)) {
                flags.push(ch);
            }
        }
    }

    return flags;
}

export function isUnameCommand(input: string): boolean {
    const trimmed = input.trim().toLowerCase();
    return trimmed === "uname" || trimmed.startsWith("uname ");
}

export function runUname(input: string, profile: UnameProfile): string {
    const flags = parseUnameArgs(input);
    if (flags === null) return "Linux";

    if (flags.length === 0 || flags.includes("s")) {
        if (flags.length === 0) return profile.sysname;
    }

    if (flags.includes("a")) {
        return [
            profile.sysname,
            profile.nodename,
            profile.release,
            profile.version,
            profile.machine,
            profile.processor,
            profile.platform,
            profile.os,
        ].join(" ");
    }

    const parts: string[] = [];
    const order = "snrvmpio";

    for (const flag of order) {
        if (!flags.includes(flag)) continue;
        if (flag === "s") parts.push(profile.sysname);
        if (flag === "n") parts.push(profile.nodename);
        if (flag === "r") parts.push(profile.release);
        if (flag === "v") parts.push(profile.version);
        if (flag === "m") parts.push(profile.machine);
        if (flag === "p") parts.push(profile.processor);
        if (flag === "i") parts.push(profile.platform);
        if (flag === "o") parts.push(profile.os);
    }

    return parts.join(" ");
}
